import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Lightbulb, Lock, Check, ArrowRight, Ticket, Loader2 } from "lucide-react";
import { GFFrame, GFHeader } from "@/components/GFFrame";
import { ScoreGauge, FactorBar } from "@/components/ScoreVisuals";
import { getAssessment, redeemMentorCode } from "@/lib/gfApi";
import { ARCHETYPE_IMAGES } from "@/data/gfContent";

const FACTOR_ORDER = ["hala_tuju", "jenama", "pasaran", "hasil", "pelaksanaan"];

const REPORT_INCLUDES = [
  "Analisis lengkap 5 Growth Factor",
  "Kekuatan utama bisnes",
  "Penghalang pertumbuhan utama",
  "Potensi kebocoran hasil",
  "Pemerhatian jenama & pemasaran",
  "Keutamaan tindakan",
  "Pelan Tindakan Pertumbuhan 30 Hari",
  "Profil archetype & keserasian",
];

export default function Results() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [showCode, setShowCode] = useState(false);

  const redeem = async () => {
    if (!code.trim()) {
      toast.error("Sila masukkan kod program.");
      return;
    }
    setRedeeming(true);
    try {
      await redeemMentorCode(id, code.trim());
      toast.success("Kod program disahkan. Laporan penuh dibuka!");
      navigate(`/report/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kod program tidak sah.");
      setRedeeming(false);
    }
  };

  useEffect(() => {
    getAssessment(id).then(setD).catch(() => setErr(true));
  }, [id]);

  useEffect(() => {
    if (d && d.paid) navigate(`/report/${id}`, { replace: true });
  }, [d, id, navigate]);

  if (err) {
    return (
      <GFFrame>
        <div className="flex min-h-screen items-center justify-center text-slate-400">
          Keputusan tidak dijumpai.
        </div>
      </GFFrame>
    );
  }
  if (!d) {
    return (
      <GFFrame>
        <div className="flex min-h-screen items-center justify-center text-slate-400">
          Memuatkan keputusan…
        </div>
      </GFFrame>
    );
  }

  return (
    <GFFrame testId="results-page">
      <div className="min-h-screen pb-12">
        <GFHeader />

        {/* Score hero */}
        <motion.section
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="px-6 pt-4 text-center"
        >
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-400">
            {d.preview?.name ? `Growth Factor ${d.preview.name}` : "Skor Growth Factor"}
          </p>
          <div className="mt-4">
            <ScoreGauge score={d.overall} />
          </div>
          <div
            data-testid="category-label"
            className="mx-auto mt-4 inline-block rounded-full bg-[#0F172A] px-5 py-2 text-sm font-bold uppercase tracking-wider text-[#D4AF37]"
          >
            {d.category.label}
          </div>
          <p className="mx-auto mt-4 max-w-xs text-sm leading-relaxed text-slate-500">
            {d.preview?.name ? `Ringkasan pantas untuk ${d.preview.name}.` : ""}
          </p>
        </motion.section>

        {/* Conversion preview narrative */}
        {d.preview && (
          <section className="mt-8 px-6" data-testid="bg-preview-narrative">
            <p className="text-left text-[15px] leading-relaxed text-slate-700">{d.preview.opening}</p>

            <div className="mt-5 overflow-hidden rounded-2xl bg-[#0F172A] p-5 text-white" data-testid="bg-preview-bird">
              <span className="text-xs font-bold uppercase tracking-widest text-[#D4AF37]">Archetype Perniagaan {d.preview.name}</span>
              <h3 className="mt-1 font-heading text-2xl font-bold text-[#D4AF37]">{d.preview.bird.name}</h3>
              {d.preview.bird.tagline && <p className="text-sm text-slate-300">{d.preview.bird.tagline}</p>}
              <p className="mt-2 text-sm text-slate-300">{d.preview.bird.why}</p>
            </div>

            <div className="mt-5">
              <span className="text-xs font-bold uppercase tracking-widest text-[#B8912B]">3 Kekuatan Pemilik</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {d.preview.strengths.map((s, i) => (
                  <span key={i} className="rounded-full bg-slate-100 px-3 py-1.5 text-sm font-semibold text-[#0F172A]">{s}</span>
                ))}
              </div>
            </div>

            <p className="mt-5 rounded-xl border-l-4 border-[#D4AF37] bg-[#FBFAF6] p-4 text-sm italic leading-relaxed text-slate-600">
              {d.preview.bottleneck_teaser}
            </p>
          </section>
        )}

        {/* Factor breakdown */}
        <section className="mt-8 px-6">
          <h3 className="font-heading text-xl font-bold text-[#0F172A]">
            Profil 5 Growth Factor
          </h3>
          <div className="mt-5 space-y-4">
            {FACTOR_ORDER.map((k) => (
              <FactorBar
                key={k}
                label={d.factor_labels[k]}
                score={d.scores[k]}
                highlight={k === d.driver.key}
              />
            ))}
          </div>
        </section>

        {/* Driver */}
        <section className="mt-8 px-6">
          <div className="rounded-2xl border border-[#E7E2D6] bg-[#FBFAF6] p-5" data-testid="growth-driver">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
              <span className="text-xs font-bold uppercase tracking-widest text-emerald-700">
                Pemacu Pertumbuhan
              </span>
            </div>
            <h4 className="mt-2 font-heading text-lg font-bold text-[#0F172A]">
              {d.driver.label}
            </h4>
          </div>
        </section>

        {/* Blocker */}
        <section className="mt-4 px-6">
          <div className="rounded-2xl border border-[#E7E2D6] bg-[#FBFAF6] p-5" data-testid="growth-blocker">
            <div className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-rose-500" />
              <span className="text-xs font-bold uppercase tracking-widest text-rose-600">
                Penghalang Pertumbuhan Utama
              </span>
            </div>
            <h4 className="mt-2 font-heading text-lg font-bold text-[#0F172A]">
              {d.blocker.label}
            </h4>
          </div>
        </section>

        {/* Archetype */}
        <section className="mt-6 px-6">
          <div className="overflow-hidden rounded-2xl bg-[#0F172A] text-white" data-testid="archetype-card">
            <div
              className="h-40 w-full bg-cover bg-center"
              style={{ backgroundImage: `url(${ARCHETYPE_IMAGES[d.archetype.image_key]})` }}
            />
            <div className="p-5">
              <span className="text-xs font-bold uppercase tracking-widest text-[#D4AF37]">
                Business Archetype
              </span>
              <h4 className="mt-1 font-heading text-2xl font-bold">{d.archetype.name}</h4>
              <p className="text-sm text-slate-400">{d.archetype.tagline}</p>
              <p className="mt-3 text-sm leading-relaxed text-slate-300">
                {d.archetype.description}
              </p>
            </div>
          </div>
        </section>

        {/* One recommendation */}
        <section className="mt-4 px-6">
          <div className="flex gap-3 rounded-2xl border border-[#D4AF37]/40 bg-[#FFFBEF] p-5">
            <Lightbulb className="h-5 w-5 shrink-0 text-[#D4AF37]" />
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#B8912B]">
                Cadangan Segera
              </span>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-700">
                {d.recommendation}
              </p>
            </div>
          </div>
        </section>

        {/* Snapshot preview teaser */}
        <section className="mt-8 px-6" data-testid="snapshot-preview-section">
          <div className="rounded-2xl border border-[#E7E2D6] bg-white p-4">
            <p className="text-xs font-bold uppercase tracking-widest text-[#B8912B]">Termasuk Percuma</p>
            <h3 className="mt-1 font-heading text-lg font-bold text-[#0F172A]">A4 Growth Snapshot — inilah rupanya</h3>
            <p className="mt-1 text-sm text-slate-500">Ringkasan satu halaman yang boleh dicetak & ditampal di dinding. Buka penuh selepas RM15.</p>
            <div className="mt-4 overflow-hidden rounded-xl border border-[#E7E2D6]" style={{ height: 420, pointerEvents: "none" }}>
              <iframe
                title="A4 Growth Snapshot Preview"
                data-testid="snapshot-preview-frame"
                src={`${process.env.REACT_APP_BACKEND_URL}/api/assessments/${id}/snapshot_preview.pdf#toolbar=0&navpanes=0&view=FitH`}
                style={{ width: "100%", height: "100%", border: 0 }}
              />
            </div>
          </div>
        </section>

        {/* Paywall */}
        <section className="mt-8 px-6">
          <div className="rounded-2xl border-2 border-[#0F172A] bg-white p-6" data-testid="paywall-card">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-[#0F172A]" />
              <span className="text-xs font-bold uppercase tracking-widest text-[#0F172A]">
                Laporan Penuh Growth Factor
              </span>
            </div>
            <div className="mt-4 flex items-end gap-2">
              <span className="font-heading text-4xl font-bold text-[#0F172A]">RM15</span>
              <span className="mb-1 rounded-full bg-[#D4AF37]/15 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-[#B8912B]">
                Harga Pengenalan
              </span>
            </div>

            <div className="mt-5 space-y-2.5">
              {REPORT_INCLUDES.map((f) => (
                <div key={f} className="flex items-start gap-2.5">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  <span className="text-sm text-slate-700">{f}</span>
                </div>
              ))}
            </div>

            <button
              data-testid="unlock-report-btn"
              onClick={() => navigate(`/report/${id}`)}
              className="group mt-6 flex w-full items-center justify-center gap-3 rounded-full bg-[#0F172A] py-4 text-base font-bold text-white transition-transform duration-200 active:scale-[0.98] hover:scale-[1.01]"
            >
              BUKA LAPORAN PENUH — RM15
              <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </button>

            {/* Mentor / Program Access Code bypass */}
            <div className="mt-5 border-t border-dashed border-[#E7E2D6] pt-4" data-testid="mentor-code-block">
              {!showCode ? (
                <button
                  data-testid="have-program-code-btn"
                  onClick={() => setShowCode(true)}
                  className="flex w-full items-center justify-center gap-2 text-sm font-semibold text-[#0F172A] underline-offset-4 hover:underline"
                >
                  <Ticket className="h-4 w-4 text-[#B8912B]" /> Ada Kod Program?
                </button>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-[#B8912B]">Kod Program Mentor</p>
                  <p className="text-sm text-slate-500">Peserta program mentor/latihan boleh membuka laporan penuh tanpa bayaran menggunakan kod yang diberikan.</p>
                  <input
                    data-testid="mentor-code-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && redeem()}
                    placeholder="cth: MENTOR-AB12CD"
                    className="w-full rounded-2xl border border-[#E7E2D6] bg-white px-4 py-3 text-[15px] font-bold uppercase tracking-widest text-[#0F172A] outline-none focus:border-[#D4AF37]"
                  />
                  <button
                    data-testid="redeem-mentor-code-btn"
                    onClick={redeem}
                    disabled={redeeming}
                    className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-[#0F172A] py-3 text-sm font-bold text-[#0F172A] transition-transform active:scale-[0.98] disabled:opacity-60"
                  >
                    {redeeming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ticket className="h-4 w-4" />} SAHKAN KOD & BUKA LAPORAN
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </GFFrame>
  );
}
